import type { ReactNode } from "react";

import { Search, X } from "lucide-react";

import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/utils";

interface DataToolbarProps {
  /** Valor del search input — controlado. */
  search?: string;
  onSearchChange?: (next: string) => void;
  /** Placeholder del search. */
  searchPlaceholder?: string;
  /** Filtros a la derecha del search — chips, selects, etc. */
  filters?: ReactNode;
  /** Acciones a la derecha final — típicamente botón "Nuevo …". */
  actions?: ReactNode;
  className?: string;
}

/**
 * Toolbar sobre un listado. Compone 3 slots:
 *
 *   [ Search (grow)  ]  [ Filters (chips) ]  [ Actions (CTA) ]
 *
 * El search tiene un botón X cuando hay valor (quick-clear). Si no se
 * pasa `search`, el slot izquierdo se omite (algunos listados tienen
 * solo filtros, no texto libre).
 */
export function DataToolbar({
  search,
  onSearchChange,
  searchPlaceholder = "Buscar…",
  filters,
  actions,
  className,
}: DataToolbarProps) {
  const hasSearch = typeof search === "string" && onSearchChange;

  return (
    <div
      className={cn(
        "mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
        {hasSearch ? (
          <div className="relative w-full sm:max-w-xs">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="search"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-10 pl-9 pr-9 text-[13.5px] transition-shadow focus-visible:shadow-ring-focus"
              aria-label="Buscar"
            />
            {search ? (
              <button
                type="button"
                onClick={() => onSearchChange("")}
                className={cn(
                  "absolute right-1.5 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md",
                  "text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                )}
                aria-label="Limpiar búsqueda"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
        ) : null}

        {filters ? <div className="flex flex-wrap items-center gap-2">{filters}</div> : null}
      </div>

      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
